import React from 'react'
import { FilterDescriptor } from '@progress/kendo-data-query'
import { GridFilterCellProps } from '@progress/kendo-react-grid'
import { LuckyDrawActivityTypeDto } from 'api/Trading-Accounts/Types/MarketingCampaignResultsDto'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchAffiliateCountries, fetchAffiliateTypes } from 'store/affiliate/affiliate.actions'
import { fetchLuckyDrawActivityTypesAction } from 'store/marketing/marketing.actions'
import { fetchAllServerAction } from 'store/trading-accounts/trading-accounts.actions'
import {
  getWalletCurrenciesAction,
  getWalletPaymentProvidersActions,
  getWalletTypesAction,
} from 'store/wallet/wallet-actions'
import { AffiliateApiClient } from 'utils/api/affiliateApi'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { isValidDate } from 'utils/date'
import { isEmpty } from 'utils/is-empty'
import { utcToZonedTime } from 'utils/time-zone/helpers'

import { DateFilter } from './DateFilter'
import { DateTimeFilter } from './DateTimeFilter'
import { multiSelectFilterCell } from './FilterCell'
import { MultiSelectFilter } from './MultiSelectFilter'
import { SelectFilter } from './SelectFilter'
import { WalletType } from './WalletFilterCell/WalletTypeFilterCell'

export interface FilterProps {
  filter: FilterDescriptor
  timezone?: DateTimeFilterTimeZoneType
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}

export const CompaniesFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  const data = tickmillCompanies.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return (
    <SelectFilter
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 200 }}
    />
  )
}

export const MultiCompanySelectFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const companies = useAppSelector((state) => state.identity.tickmillCompanies)

  const data = companies.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return (
    <MultiSelectFilter
      placeholder='Companies'
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 200 }}
    />
  )
}

export const PlatformFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)

  const data = platformTypes.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

export const CustomDateFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props

  return <DateFilter filter={filter} onFilterChange={onFilterChange} />
}

export const DateOnlyFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props

  return <DateFilter filter={filter} onFilterChange={onFilterChange} isDateOnly />
}

export enum DateTimeFilterTimeZoneType {
  UTC = 'utc',
  LOCALE = 'locale',
}

export interface CustomDateTimeFilterProps {
  filter: FilterDescriptor
  timezone?: DateTimeFilterTimeZoneType
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}

export const CustomDateTimeFilter: React.FC<FilterProps> = (props) => {
  const { filter, timezone = DateTimeFilterTimeZoneType.UTC, onFilterChange } = props

  const filterDescriptor = {
    ...filter,
    value: formatDateTimeFilterValue(filter.value, timezone),
  }

  return <DateTimeFilter filter={filterDescriptor} onFilterChange={onFilterChange} />
}

const formatDateTimeFilterValue = (date: Date | string, timezone: DateTimeFilterTimeZoneType) => {
  const value = date instanceof Date ? date.toISOString() : date

  if (timezone === DateTimeFilterTimeZoneType.UTC) {
    if (date && date instanceof Date) {
      return date
    }

    if (date && isValidDate(value)) {
      return utcToZonedTime(value)
    }
  }

  if (timezone === DateTimeFilterTimeZoneType.LOCALE) {
    if (date && isValidDate(value)) {
      return new Date(value)
    }
  }
}

export const CountryFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const countries = useAppSelector((state) => state.identity.countries)

  const data = countries.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

export const MultiCountrySelectFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const countries = useAppSelector((state) => state.identity.countries)

  const data = countries.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return (
    <MultiSelectFilter
      placeholder='Countries'
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 350 }}
    />
  )
}

export const MultiSelectPaymentProviderFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const dispatch = useAppDispatch()
  const walletsApiClient = useApiClient(WalletsApiClient)
  const paymentProviderDtos = useAppSelector((state) => state.wallet.paymentProviders)
  if (isEmpty(paymentProviderDtos)) {
    dispatch(getWalletPaymentProvidersActions(walletsApiClient))
  }
  const data = paymentProviderDtos.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return (
    <MultiSelectFilter
      placeholder='Payment Providers'
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 350 }}
    />
  )
}

const WalletCurrency = [
  { id: 'USD', label: 'USD', value: 'USD' },
  { id: 'EUR', label: 'EUR', value: 'EUR' },
  { id: 'GBP', label: 'GBP', value: 'GBP' },
  { id: 'PLN', label: 'PLN', value: 'PLN' },
  { id: 'CHF', label: 'CHF', value: 'CHF' },
  { id: 'ZAR', label: 'ZAR', value: 'ZAR' },
  { id: 'SGD', label: 'SGD', value: 'SGD' },
  { id: 'HKD', label: 'HKD', value: 'HKD' },
  { id: 'MAD', label: 'MAD', value: 'MAD' },
]

export const WalletCurrencyFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={WalletCurrency} />
}

export const MultiClientProductSelectFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const tickmillProducts = useAppSelector((state) => state.clients.tickmillProducts)

  const data = tickmillProducts.map((x) => ({ id: x.id, label: x.name, value: x.id }))
  return (
    <MultiSelectFilter
      placeholder='Products'
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 200 }}
    />
  )
}

export const MultiWalletTypeSelectFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const data = WalletType.map((x) => ({ id: x.value, label: x.text, value: x.value }))
  return (
    <MultiSelectFilter
      placeholder='Wallet Type'
      filter={filter}
      onFilterChange={onFilterChange}
      data={data}
      style={{ width: 200 }}
    />
  )
}

export const ServerFilter = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const servers = useAppSelector((state) => state.tradingAccounts.allServers)
  const dispatch = useAppDispatch()
  const tradingAccountsApi = useApiClient(TradingAccountsApiClient)
  if (isEmpty(servers)) {
    dispatch(fetchAllServerAction(tradingAccountsApi))
  }

  const data = servers.map((x) => ({ id: x.id, label: x.name, value: x.id }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

export const ServerPublicNamesFilter = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const servers = useAppSelector((state) => state.tradingAccounts.allServers)
  const dispatch = useAppDispatch()
  const tradingAccountsApi = useApiClient(TradingAccountsApiClient)
  if (isEmpty(servers)) {
    dispatch(fetchAllServerAction(tradingAccountsApi))
  }

  const data = servers.map((x) => ({ id: x.id, label: x.publicName, value: x.id }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

export const CurrencyMultiSelectFilterCell: React.FC<GridFilterCellProps> = (props) => {
  const currencies = useAppSelector((state) => state.wallet.currencies)
  const dispatch = useAppDispatch()
  const walletsApiClient = useApiClient(WalletsApiClient)

  if (isEmpty(currencies)) {
    dispatch(getWalletCurrenciesAction(walletsApiClient))
  }
  return multiSelectFilterCell({
    items: currencies.map((x) => ({ id: x.id, value: x.id, label: x.id })),
  })(props)
}

export const WalletTypeMultiSelectFilterCell: React.FC<GridFilterCellProps> = (props) => {
  const walletTypes = useAppSelector((state) => state.wallet.walletType)
  const dispatch = useAppDispatch()
  const walletsApiClient = useApiClient(WalletsApiClient)

  if (isEmpty(walletTypes)) {
    dispatch(getWalletTypesAction(walletsApiClient))
  }

  return multiSelectFilterCell({
    items: walletTypes.map((x) => ({ id: x.id, value: x.name, label: x.name })),
  })(props)
}

export const AffiliateTypeFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const affiliateApiClient = useApiClient(AffiliateApiClient)
  const affiliateTypes = useAppSelector((state) => state.affiliates.types)

  const dispatch = useAppDispatch()
  if (isEmpty(affiliateTypes)) {
    dispatch(fetchAffiliateTypes(affiliateApiClient))
  }

  const data = affiliateTypes.map(({ id, name }) => ({
    id: id,
    value: id,
    label: name,
  }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

export const AffiliateCountryFilter: React.FC<FilterProps> = (props) => {
  const { filter, onFilterChange } = props
  const affiliateApiClient = useApiClient(AffiliateApiClient)
  const affiliateCountries = useAppSelector((state) => state.affiliates.countries)

  const dispatch = useAppDispatch()

  if (isEmpty(affiliateCountries)) {
    dispatch(fetchAffiliateCountries(affiliateApiClient))
  }
  const data = affiliateCountries.map(({ id, name }) => ({ id, label: name, value: id }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

export const BaseURLFilter = ({ filter, onFilterChange }: FilterProps) => {
  const baseUrlTypes = useAppSelector((state) => state.clientArea.baseUrlTypes)
  const data = baseUrlTypes.map((url) => ({
    id: url.id,
    label: url.name,
    value: url.id,
  }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}

export const LuckyDrawActivityFilter = ({
  filter,
  onFilterChange,
}: {
  filter: FilterDescriptor
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
}) => {
  const luckyDrawActivityTypes = useAppSelector((state) => state.marketing.luckyDrawActivityTypes)
  const dispatch = useAppDispatch()
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  if (isEmpty(luckyDrawActivityTypes)) {
    dispatch(fetchLuckyDrawActivityTypesAction(tradingAccountsApiClient))
  }

  const data = (luckyDrawActivityTypes || []).map((t: LuckyDrawActivityTypeDto) => ({
    id: t.id,
    label: t.name,
    value: t.id,
  }))

  return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
}
